import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../Food/RecipeList.css";
import "./BabyTracker.css";
import {
  deleteMedicineReminder,
  fetchMedicineReminders,
  fetchBabyPushPublicKey,
  medicineTypes,
  saveBabyPushSubscription,
  saveMedicineReminder,
  sendBabyPushTest,
} from "./BabyTrackerShared";
import BabyTrackerLoading from "./BabyTrackerLoading";
import { browserSupportsBabyPush, subscribeToBabyPush } from "./babyPushRegistration";

const defaultReminderForm = {
  medicineName: medicineTypes[0],
  otherMedicineName: "",
};

const reminderNameFromForm = (form) => (
  form.medicineName === "Other" ? form.otherMedicineName.trim() : form.medicineName
);

const BabyTrackerMedicineReminders = () => {
  const [reminders, setReminders] = useState([]);
  const [form, setForm] = useState(defaultReminderForm);
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState("");
  const [pushLoading, setPushLoading] = useState(false);
  const [pushStatusMessage, setPushStatusMessage] = useState("");

  const refreshReminders = () => {
    setLoading(true);
    fetchMedicineReminders()
      .then((data) => {
        setReminders(data || []);
        setLoading(false);
      })
      .catch(() => {
        setReminders([]);
        setLoading(false);
      });
  };

  useEffect(() => {
    refreshReminders();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((currentValue) => ({
      ...currentValue,
      [name]: value,
      otherMedicineName: name === "medicineName" && value !== "Other" ? "" : currentValue.otherMedicineName,
    }));
  };

  const addReminder = (event) => {
    event.preventDefault();
    const medicineName = reminderNameFromForm(form);
    if (!medicineName) {
      setStatusMessage("Enter a medicine name.");
      return;
    }

    setStatusMessage("");
    saveMedicineReminder({ medicineName, active: true })
      .then(() => {
        setForm(defaultReminderForm);
        setStatusMessage("Medicine reminder saved.");
        refreshReminders();
      })
      .catch(() => {
        setStatusMessage("Unable to save medicine reminder.");
      });
  };

  const toggleReminder = (reminder) => {
    saveMedicineReminder(
      { medicineName: reminder.medicineName, active: !reminder.active },
      reminder.id
    )
      .then(() => {
        setStatusMessage("Medicine reminder updated.");
        refreshReminders();
      })
      .catch(() => {
        setStatusMessage("Unable to update medicine reminder.");
      });
  };

  const removeReminder = (reminderId) => {
    deleteMedicineReminder(reminderId)
      .then(() => {
        setStatusMessage("Medicine reminder deleted.");
        refreshReminders();
      })
      .catch(() => {
        setStatusMessage("Unable to delete medicine reminder.");
      });
  };

  const enablePushReminders = async () => {
    setPushLoading(true);
    setPushStatusMessage("");
    try {
      if (!browserSupportsBabyPush()) {
        throw new Error("This browser does not support phone reminders.");
      }

      const keySettings = await fetchBabyPushPublicKey();
      if (!keySettings.enabled || !keySettings.publicKey) {
        throw new Error("Phone reminders are not configured on the server yet.");
      }

      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error("Notifications were not allowed for this site.");
      }

      const subscription = await subscribeToBabyPush(keySettings.publicKey);
      await saveBabyPushSubscription(subscription.toJSON());
      setPushStatusMessage("Phone reminders enabled for this browser.");
    } catch (error) {
      setPushStatusMessage(error.message || "Unable to enable phone reminders.");
    } finally {
      setPushLoading(false);
    }
  };

  const sendTestNotification = async () => {
    setPushLoading(true);
    setPushStatusMessage("");
    try {
      const result = await sendBabyPushTest();
      setPushStatusMessage(`Test sent to ${result.sentCount || 0} browser${result.sentCount === 1 ? "" : "s"}.`);
    } catch (error) {
      setPushStatusMessage(error.message || "Unable to send test notification.");
    } finally {
      setPushLoading(false);
    }
  };

  return (
    <div className="BabyTrackerPage">
      <div className="searchBarDiv formViewWrapper BabyTrackerIntro">
        <div>
          <h2 className="BabyTrackerTitle">Medicine reminders</h2>
          <p className="BabyTrackerSubtitle">
            Choose daily medicines that should appear on the home page until logged.
          </p>
        </div>
        <Link className="summaryButton BabyTrackerLinkButton" to="/baby-tracker">
          Home
        </Link>
      </div>

      <div className="BabyTrackerGrid">
        <div className="searchBarDiv formViewWrapper BabyTrackerPanel BabyTrackerSinglePanel">
          <h2 className="BabyTrackerTitle">Add reminder</h2>
          <form onSubmit={addReminder}>
            <div className="BabyTrackerTwoColumn">
              <div className="inputDiv">
                <label>Medicine</label>
                <select
                  className="inputBox addInput"
                  name="medicineName"
                  value={form.medicineName}
                  onChange={handleChange}
                >
                  {medicineTypes.map((medicineType) => (
                    <option key={medicineType} value={medicineType}>
                      {medicineType}
                    </option>
                  ))}
                </select>
              </div>
              {form.medicineName === "Other" ? (
                <div className="inputDiv">
                  <label>Medicine name</label>
                  <input
                    className="inputBox addInput"
                    name="otherMedicineName"
                    value={form.otherMedicineName}
                    onChange={handleChange}
                  />
                </div>
              ) : null}
            </div>
            <div className="BabyTrackerActionRow">
              <button type="submit" className="summaryButton">
                Add
              </button>
            </div>
          </form>
          {statusMessage ? <p className="BabyTrackerStatus">{statusMessage}</p> : null}
        </div>

        <div className="searchBarDiv formViewWrapper BabyTrackerPanel BabyTrackerSinglePanel">
          <h2 className="BabyTrackerTitle">Phone reminders</h2>
          <p className="BabyTrackerSubtitle">
            Enable reminders on this browser, then use the test button before adding the nightly cron job.
          </p>
          <div className="BabyTrackerActionRow">
            <button
              type="button"
              className="summaryButton"
              onClick={enablePushReminders}
              disabled={pushLoading}
            >
              Enable
            </button>
            <button
              type="button"
              className="summaryButton"
              onClick={sendTestNotification}
              disabled={pushLoading}
            >
              Send test
            </button>
          </div>
          {pushStatusMessage ? <p className="BabyTrackerStatus">{pushStatusMessage}</p> : null}
        </div>

        <div className="shoppingListDiv BabyTrackerPanel">
          <h2 className="BabyTrackerTitle">Daily reminder list</h2>
          {loading ? <BabyTrackerLoading label="Loading reminders" /> : null}
          {!loading && reminders.length ? (
            reminders.map((reminder) => (
              <div className="BabyTrackerHistoryCard" key={reminder.id}>
                <div className="BabyTrackerHistoryRow">
                  <div>
                    <strong>{reminder.medicineName}</strong>
                    <div>{reminder.active ? "Shows on home page until logged today" : "Paused"}</div>
                  </div>
                  <span>{reminder.active ? "Active" : "Inactive"}</span>
                </div>
                <div className="BabyTrackerActionRow">
                  <button
                    className="summaryButton"
                    onClick={() => toggleReminder(reminder)}
                  >
                    {reminder.active ? "Pause" : "Resume"}
                  </button>
                  <button
                    className="summaryButton"
                    onClick={() => removeReminder(reminder.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          ) : null}
          {!loading && !reminders.length ? <p>No medicine reminders configured yet.</p> : null}
        </div>
      </div>
    </div>
  );
};

export default BabyTrackerMedicineReminders;
